import useRecaptcha from '@/composables/useRecaptcha';
import authService from '@/services/auth.service';
import { defineStore } from 'pinia';

export type ModalState = 'login' | 'reset-password' | 'password-reset-sent';

export const useAuthDialogStore = defineStore('auth-dialog', () => {
    const recaptcha = useRecaptcha();

    const modalState = ref<ModalState>('login');
    const loginCredentials = ref({ email: '', password: '' });

    const resetPasswordEmail = ref('');

    async function getCaptcha() {
        const token = await recaptcha.execute();
        if (!token) throw new Error('Cannot get captcha token');
        return token;
    }

    function resetModalState() {
        modalState.value = 'login';
        loginCredentials.value = { email: '', password: '' };
        resetPasswordEmail.value = '';
    }

    async function login() {
        try {
            const session = await authService.login({
                ...loginCredentials.value,
                captcha_response: await getCaptcha(),
            });

            return !!session;
        } catch (e) {
            console.error(e);
        } finally {
            recaptcha.close();
        }

        return false;
    }

    async function sendPasswordResetLink() {
        try {
            const { status } = await authService.forgotPassword({
                email: resetPasswordEmail.value,
                captcha_response: await getCaptcha(),
            });

            return status === 204;
        } catch (e) {
            console.error(e);
        } finally {
            recaptcha.close();
        }

        return false;
    }

    return {
        modalState,
        loginCredentials,
        resetPasswordEmail,
        // Actions
        resetModalState,
        login,
        sendPasswordResetLink,
    };
});
